import React, { useState, useEffect, useRef } from 'react';

// Global state to track active tooltip
let activeTooltipId = null;
const tooltipListeners = new Set();

const notifyTooltipChange = (newId) => {
  activeTooltipId = newId;
  tooltipListeners.forEach(listener => listener(newId));
};

// Hook to detect if device supports hover (has mouse/trackpad)
export const useHasHover = () => {
  const [hasHover, setHasHover] = useState(true);

  useEffect(() => {
    const checkHover = () => {
      // Check if device supports hover (has mouse/trackpad)
      const supportsHover = window.matchMedia('(hover: hover)').matches;
      setHasHover(supportsHover);
    };

    checkHover();
    // Listen for changes (e.g., connecting/disconnecting mouse)
    const mediaQuery = window.matchMedia('(hover: hover)');
    mediaQuery.addEventListener('change', checkHover);
    return () => mediaQuery.removeEventListener('change', checkHover);
  }, []);

  return hasHover;
};

// Hook to detect mobile/touch device (includes tablets)
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Check for touch capability OR small screen
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      // Consider mobile if small screen OR touch-only device (no mouse)
      // For tablets with large screens, check if it's primarily touch-based
      const isTouchPrimary = hasTouch && window.matchMedia('(hover: none)').matches;
      setIsMobile(isSmallScreen || isTouchPrimary);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Component for mobile-friendly tooltips (click instead of hover)
export const MobileTooltip = ({ children, title, style = {} }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useIsMobile();
  const hasHover = useHasHover(); // Detect if device has mouse
  const tooltipId = useRef(Symbol());
  const containerRef = useRef(null);

  const handleClick = (e) => {
    if (title) {
      e.stopPropagation();
      
      // If already showing, toggle off
      if (showTooltip) {
        setShowTooltip(false);
        notifyTooltipChange(null);
      } else {
        // Close any other open tooltips
        notifyTooltipChange(tooltipId.current);
        setShowTooltip(true);
        
        // Auto-hide after 3 seconds on mobile, 5 seconds on desktop
        const timeout = isMobile ? 3000 : 5000;
        setTimeout(() => {
          if (activeTooltipId === tooltipId.current) {
            setShowTooltip(false);
            notifyTooltipChange(null);
          }
        }, timeout);
      }
    }
  };

  // On devices with mouse: close tooltip when mouse leaves
  const handleMouseLeave = () => {
    if (hasHover && showTooltip) {
      setShowTooltip(false);
      notifyTooltipChange(null);
    }
  };

  // Listen for other tooltips opening
  useEffect(() => {
    const listener = (newId) => {
      if (newId !== tooltipId.current) {
        setShowTooltip(false);
      }
    };

    tooltipListeners.add(listener);
    return () => tooltipListeners.delete(listener);
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;

    const handleOutsideClick = (e) => {
      // Check if click is outside the tooltip container
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowTooltip(false);
        notifyTooltipChange(null);
      }
    };
    
    // Small delay to avoid immediate closing on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick);
    }, 10);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showTooltip]);

  // On desktop: show title on hover if custom tooltip is not showing
  const titleAttr = !isMobile && !showTooltip ? title : undefined;

  return (
    <span
      ref={containerRef}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      title={titleAttr}
      style={{ ...style, position: 'relative', cursor: title ? 'pointer' : 'default' }}
    >
      {children}
      {showTooltip && title && (
        <span
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            padding: '8px 12px',
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderRadius: '6px',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            zIndex: 10000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            maxWidth: '300px',
            whiteSpace: 'normal',
            textAlign: 'center',
            lineHeight: '1.4'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {title}
          <span
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(0, 0, 0, 0.9)'
            }}
          />
        </span>
      )}
    </span>
  );
};

export default MobileTooltip;
